import { Tooltip } from "@/components/basic";
import { LoadingSpinner } from "@/components/util/LoadingSpinner";
import { useCustomParkingVerbiageOnWeb as useCustomParkingVerbiage } from "@/hooks/useCustomParkingVerbiage";
import {
  RESERVE_TEXTS,
  SaveReservationData,
  SaveReservationDataUsers
} from "@/lib/utils-reserve";
import { IResource, Resource, ResourceType } from "@gettactic/api";
import { useAreas } from "@gettactic/hooks";
import { useMemo } from "react";
import { FieldErrors, UseFormReturn } from "react-hook-form";
import { Controller } from "react-hook-form";
import ReactSelect, { components } from "react-select";
import {
  Select,
  SelectTrigger,
  SelectItem,
  SelectContent,
  SelectValue
} from "@cmp-src/primitives/Select";
import { useIsMobile } from "@v2/hooks/use-mobile";

export type NarrowResourceTypes = "meeting_room" | "workspace" | "parking";

type ReserveFieldResourcesListProps = {
  resourceType: ResourceType;
  form: UseFormReturn<SaveReservationData>;
  resourcesList: Resource[] | null | undefined;
  defaultValue: Resource | null;
  officeId: string;
  errors: FieldErrors<SaveReservationData>;
};

export function isOverCapacity(
  users: SaveReservationDataUsers,
  resource: IResource | null = null
) {
  return !!users && !!resource && users.length > resource.available_slots;
}

type TooltipOverCapacityProps = {
  users: SaveReservationDataUsers;
  resource: IResource;
  entity: NarrowResourceTypes;
};

export function TooltipOverCapacity({
  users,
  resource,
  entity
}: TooltipOverCapacityProps) {
  let visualEntity: NarrowResourceTypes | "locker" = entity;
  const customParkingVerbiage = useCustomParkingVerbiage();
  if (customParkingVerbiage && entity === "parking") {
    visualEntity = "locker";
  }
  return !!resource && !!users ? (
    <div className="mt-2 flex max-w-[155px] items-center rounded-xl bg-yellow-50 px-2 py-1">
      <span className="text-yellow-800">Over Capacity</span>
      <Tooltip testId="capacity-tooltip">
        <div className="w-48 font-semibold">
          {RESERVE_TEXTS.overCapacityTitle[visualEntity]}
        </div>
        <div className="text-sm">
          {RESERVE_TEXTS.overCapacityTotalSlots[visualEntity]}:&nbsp;
          {resource.available_slots}
        </div>
        <div className="text-sm">
          {RESERVE_TEXTS.overCapacityUsersLength[visualEntity]}:&nbsp;
          <span className="text-primary" data-testid="users-length">
            {users.length}
          </span>
        </div>
      </Tooltip>
    </div>
  ) : null;
}

export default function ReserveFieldResourcesList({
  resourcesList,
  defaultValue: def,
  form,
  resourceType: resType,
  officeId,
  errors
}: ReserveFieldResourcesListProps) {
  const isMobile = useIsMobile();

  const resourceType: NarrowResourceTypes = useMemo(() => {
    if (resType === "parking_lot") {
      return "parking";
    }
    if (resType === "parking_space") {
      return "parking";
    }
    if (["meeting_room", "workspace"].includes(resType)) {
      return resType as NarrowResourceTypes;
    }
    return "workspace";
  }, [resType]);

  const { areas } = useAreas(officeId);
  const users = form.watch("users");
  const resource = form.watch("resource");
  const showTooltipOverCapacity = isOverCapacity(users, resource);
  const options = useMemo(
    () =>
      (resourcesList ?? []).map((res) => ({
        ...res,
        label: res.name,
        value: res.id
      })),
    [resourcesList]
  );
  const defaultValue = useMemo(
    () => options.find((res) => res.id === def?.id),
    [def, options]
  );

  const showEmpty = options.length === 0 && !defaultValue;
  if (!resourcesList) {
    return (
      <LoadingSpinner color="text-secondary [.tactic-v2_&]:text-secondary-foreground" />
    );
  }
  const isParking = resourceType === "parking";
  const customParkingVerbiage = useCustomParkingVerbiage();

  let fieldPlaceholder = RESERVE_TEXTS.selectResource[resourceType];
  let parkingEmptyMessage = "parking spaces";
  if (isParking && customParkingVerbiage) {
    fieldPlaceholder = `Select ${customParkingVerbiage}`;
    parkingEmptyMessage = customParkingVerbiage;
  }

  return (
    <>
      {showEmpty ? (
        <p className="text-primary">{` No ${isParking ? parkingEmptyMessage : resourceType === "meeting_room" ? "rooms" : "desks"} available to reserve!`}</p>
      ) : (
        <>
          <Controller
            control={form.control}
            name={"resource"}
            rules={{ required: true }}
            defaultValue={defaultValue}
            render={({ field: { onChange, value } }) => {
              const getValueContent = (props: {
                data: (typeof options)[0];
              }) => {
                return (
                  <>
                    {props.data.name}
                    {props.data.available_slots > 0 && (
                      <span className="ml-2 rounded-full bg-green-100 px-2.5 py-0.5 font-medium text-green-800 text-xs">
                        {props.data.available_slots}
                      </span>
                    )}
                    {areas &&
                    props.data.parent_id &&
                    areas.byId[props.data.parent_id] ? (
                      <span className="ml-1 rounded-md bg-gray-100 px-2.5 py-0.5 text-xs">
                        {areas.byId[props.data.parent_id].name}
                      </span>
                    ) : null}
                    {Array.isArray(props.data.tags) &&
                      props.data.tags.length > 0 &&
                      props.data.tags.map((tag) => {
                        return (
                          <span
                            key={tag.id}
                            className="ml-1 rounded-md bg-gray-200 px-2.5 py-0.5 text-xs"
                          >
                            {tag.name}
                          </span>
                        );
                      })}
                  </>
                );
              };

              return isMobile ? (
                <Select
                  value={value?.id}
                  onValueChange={(value) => {
                    onChange(options.find((res) => res.id === value));
                  }}
                >
                  <SelectTrigger variant={"gray"} className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {options?.map((option) => (
                      <SelectItem
                        key={option.id}
                        value={option.id}
                        disabled={option.type === "area"}
                      >
                        {getValueContent({ data: option })}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <ReactSelect
                  menuPortalTarget={document.body}
                  menuPosition={"fixed"}
                  onChange={onChange}
                  value={value}
                  placeholder={fieldPlaceholder}
                  className={"resource-select"}
                  classNamePrefix="react-select"
                  isOptionDisabled={(option) => option.type === "area"}
                  noOptionsMessage={() => "No availability"}
                  components={{
                    GroupHeading: (props) => (
                      <div className="mx-2 h-8 border-gray-50 border-b-2 pl-2 font-bold">
                        {props.data.label}
                      </div>
                    ),
                    Option: (props) => (
                      <components.Option {...props}>
                        {getValueContent({ data: props.data as never })}
                      </components.Option>
                    )
                  }}
                  options={options}
                />
              );
            }}
          />

          {errors.resource && errors.resource.type === "required" && (
            <p className="pl-1 text-primary">{fieldPlaceholder}</p>
          )}
          {errors.resource && errors.resource.type !== "required" && (
            <p className="text-primary">
              Hmm, something went wrong. Can you try again?
            </p>
          )}
          {showTooltipOverCapacity && (
            <TooltipOverCapacity
              users={users}
              resource={resource}
              entity={resourceType}
            />
          )}
        </>
      )}
    </>
  );
}
